"use client";

import BroadbandFactsWebComponent from "@/components/BroadbandFactsWebComponent";
import CTAGroup from "@/components/CTAGroup";
import type { CTAGroupItem } from "@/components/CTAGroup";
import LazyIcon from "@/components/icons/LazyIcon";
import IconCaretButtonDown from "@/components/icons/IconCaretButtonDown";
import IconCaretButtonUp from "@/components/icons/IconCaretButtonUp";
import IconCheck from "@/components/icons/IconCheck";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import IconListChecked from "@/components/icons/IconListChecked";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import { resolveCtaCopy, resolveCtaUrl } from "@/lib/cta-resolution";
import { useElementHeight } from "@/hooks/useEqualHeights";
import { handleError } from "@/lib/newrelic";
import { parseMetadata } from "@/lib/parse-metadata";
import { buildBundledOffersUrl } from "@/lib/clickthrough-destination";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import fireProductClicked from "@/lib/tagular/fire-product-clicked";
import { trackProductSelection } from "@/lib/tracking/productSelection";
import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import BundleProductImage from "./BundleProductImage";
import { PRICE_PRODUCT_CARD_IMAGES } from "./images";
import type { CardCTA, PriceProductCardProps } from "./types";

type PriceProductCardInternalProps = PriceProductCardProps & {
  onTopBannerHeightChange?: never;
};

const PriceProductCard: React.FC<PriceProductCardInternalProps> = ({
  offerId,
  hasProductSelection = false,
  hasPriceSection = true,
  speed,
  unit,
  topBanner,
  pricing,
  strikethroughPrice,
  tagline,
  features,
  featuresColor = "purple",
  ctas = [],
  onCtaModalClick,
  localizationType = "none",
  legalDisclaimer,
  pricingView,
  customPricingLabel,
  textAlign = "left",
  index = 0,
  heightManager,
  sessionId,
  swiper,
  tagularProps,
  badgeCopy,
  extendedTitle,
  featuresIcon = "checkmarks",
  cardStyle = "default",
  bundles,
  mobileTopCard,
  ctaBorderColor = "gray",
  elementName,
  sectionElementName,
  mobileBadgeCopy,
  mobileMostPopularTag,
  mobileFeaturedGridCtaCopy,
  mobileFeaturedGridIcon,
  mobileNoPricePostLegalCopy,
  mobileFeaturedGridBullets,
  mobileDesignVariant = "default",
  mobileFeaturedGridVariant = "default",
  designVariant = "default",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contextMonarchMetadata = useMonarchMetadata();
  const resolvedMonarchMetadata =
    tagularProps?.monarchMetadata ?? contextMonarchMetadata;

  // Separate refs: one for Tagular tracking (card wrapper), one for height management (features list)
  const cardRef = React.useRef<HTMLDivElement>(null);
  const { elementRef: rtbRef } = useElementHeight(index, heightManager, [
    tagline,
    pricingView,
    features.length,
    legalDisclaimer,
    speed,
    unit,
    extendedTitle,
  ]);

  // Calculate display price based on toggle
  const displayPrice =
    pricingView === "5year"
      ? (pricing.fiveYearPrice ?? pricing.oneYearPrice)
      : pricing.oneYearPrice;
  const cents = Math.round((displayPrice % 1) * 100);
  const hasCents = cents > 0;

  const resolvedCardName = elementName || `card${speed}${unit}`;
  const trackingDescriptor = sectionElementName
    ? `${sectionElementName}${resolvedCardName}`
    : resolvedCardName;
  const cardElementName = `cardProductCards${trackingDescriptor}`;

  const isFullyLocalized = localizationType === "full";

  const ctaNameBase = `ctaButtonProductCards${trackingDescriptor}`;
  const mobilePricingLabel =
    customPricingLabel || (pricingView === "5year" ? "5 years" : "1 year");
  const cardTitle = extendedTitle || `${speed} ${unit}`;
  const mobileMostPopularLabel =
    mobileBadgeCopy?.trim() || mobileMostPopularTag?.trim();
  const mobileFeaturedGridCtaCopyLabel = mobileFeaturedGridCtaCopy?.trim();
  const isFeaturedGridMobileDesign = mobileDesignVariant === "featured-grid";
  const isFeaturedCard = Boolean(mobileTopCard);
  const isNonPricePointVariant =
    mobileFeaturedGridVariant === "non-price-point";
  const isDesktopNonPricePointVariant =
    !isFeaturedGridMobileDesign && isNonPricePointVariant;
  const shouldShowPriceSection =
    hasPriceSection && !(isFeaturedGridMobileDesign && isNonPricePointVariant);
  const mobileLegalDisclaimer = legalDisclaimer?.trim();
  const hasMobileNoPricePostLegalCopy =
    React.Children.toArray(mobileNoPricePostLegalCopy).length > 0;
  const mobileFeaturedGridButtonClassName =
    "body-sm-semibold h-11 w-18 justify-center px-0";
  const shouldUseFeaturedGridCtaCopy =
    isFeaturedGridMobileDesign && Boolean(mobileFeaturedGridCtaCopyLabel);
  const defaultOutlineCtaTone =
    ctaBorderColor === "purple" ? "theme1" : "neutral";

  const resolvedCtas: CTAGroupItem[] = ctas.map(
    (ctaButton: CardCTA, ctaIndex: number) => {
      const displayCopy = resolveCtaCopy(ctaButton, { localizationType });
      const displayUrl = resolveCtaUrl(ctaButton, {
        localizationType,
        pricingView,
      });
      const isLinkAction = ctaButton.action === "link";

      // Monarch must explicitly enable product selection on this card before
      // its configured CTA URL can be replaced with the offer deep link.
      const shouldDeepLinkOffer =
        isLinkAction && isFullyLocalized && hasProductSelection;
      const trimmedOfferId = offerId?.trim();
      let resolvedUrl = displayUrl;
      let handleCtaClick: (() => void) | undefined;

      if (shouldDeepLinkOffer) {
        const bundledUrl = buildBundledOffersUrl(trimmedOfferId);

        if (bundledUrl && trimmedOfferId) {
          resolvedUrl = bundledUrl;
          handleCtaClick = () => {
            // Fires the CORE ProductClicked event in addition to (never
            // replacing) the ElementClicked event fired by CTAGroup.
            fireProductClicked({
              productId: trimmedOfferId,
              name: cardTitle,
              price: displayPrice,
              location: "productCards",
            });
            trackProductSelection({
              productId: trimmedOfferId,
              price: displayPrice,
              title: cardTitle,
            });
          };
        } else {
          handleError(new Error("Failed to build bundledOffers URL"), {
            source: "productCardBundledOffersUrl",
            severity: "low",
            context: { offerId: offerId ?? "", speed, unit },
          });
        }
      }

      return {
        ...ctaButton,
        copy: displayCopy,
        url: resolvedUrl,
        localizedCopy: undefined,
        localizedUrl: undefined,
        oneYearUrl: undefined,
        fiveYearUrl: undefined,
        localizedOneYearUrl: undefined,
        localizedFiveYearUrl: undefined,
        onClick: handleCtaClick,
        tagularProps: {
          ...tagularProps,
          actionOutcome: isLinkAction
            ? isFullyLocalized
              ? "sentToOffers"
              : "sentToLocalize"
            : "modalOpened",
          name: `${ctaNameBase}${isLinkAction ? "Link" : "Modal"}`,
          position: ctaIndex + 1,
          location: "productCards",
          text: displayCopy,
          outboundUrl: isLinkAction ? resolvedUrl : undefined,
          ...(resolvedMonarchMetadata && {
            monarchMetadata: resolvedMonarchMetadata,
          }),
        },
        modalKey: ctaButton.modalKeyId || ctaButton.modalKey,
      };
    }
  );
  const defaultFeaturedGridCtaTone = isNonPricePointVariant
    ? defaultOutlineCtaTone
    : "neutral-dark";
  const featuredGridVariantCtas: CTAGroupItem[] = resolvedCtas.map((cta) => ({
    ...cta,
    copy: shouldUseFeaturedGridCtaCopy
      ? mobileFeaturedGridCtaCopyLabel!
      : cta.copy,
    display: isNonPricePointVariant ? "outline" : "fill",
    tone: cta.tone ?? defaultFeaturedGridCtaTone,
    buttonWidth: "auto",
    tagularProps: shouldUseFeaturedGridCtaCopy
      ? {
          ...cta.tagularProps,
          text: mobileFeaturedGridCtaCopyLabel,
        }
      : cta.tagularProps,
  }));
  const renderFeaturedGridVariantCtas = ({
    buttonClassName = mobileFeaturedGridButtonClassName,
    defaultSize = "md",
    wrapperClassName,
  }: {
    buttonClassName?: string;
    defaultSize?: "sm" | "md";
    wrapperClassName?: string;
  }) => {
    if (featuredGridVariantCtas.length === 0) {
      return null;
    }

    return (
      <div className={wrapperClassName}>
        <CTAGroup
          ctas={featuredGridVariantCtas}
          buttonClassName={buttonClassName}
          defaultDisplay="fill"
          defaultTone={defaultFeaturedGridCtaTone}
          defaultSize={defaultSize}
          orientation="vertical"
          alignment="left"
          wrap={false}
          location="productCards"
          onModalOpen={onCtaModalClick}
        />
      </div>
    );
  };

  // Tagular tracking on card wrapper
  useElementViewed(
    cardRef,
    {
      elementType: "card",
      location: "productCards",
      name: cardElementName,
      ...tagularProps,
    },
    {
      threshold: 0.25,
    }
  );

  // Handle Swiper updates when broadband facts component resizes
  const handleBroadbandFactsResize = () => {
    if (swiper) {
      swiper.updateSize();
      swiper.updateSlides();
      swiper.update();
    }
  };

  useEffect(() => {
    if (swiper) {
      setTimeout(() => {
        swiper.updateSize();
        swiper.updateSlides();
        swiper.update();
      }, 200);
    }
  }, [isExpanded, swiper]);

  // Handle toggle of broadband labels drawer
  const handleBroadbandToggle = () => {
    const newExpandedState = !isExpanded;

    // Fire analytics event
    fireElementClicked({
      actionOutcome: newExpandedState ? "detailsExpanded" : "detailsCollapsed",
      elementType: "ctaButton",
      text: newExpandedState ? "Read More" : "Read Less",
      name: `ctaButtonProductCardsBroadbandFacts${trackingDescriptor}`,
      location: "productCards",
      ...tagularProps,
      ...(resolvedMonarchMetadata && {
        monarchMetadata: resolvedMonarchMetadata,
      }),
    });

    setIsExpanded(newExpandedState);
  };

  // Determine if this is a lavender card
  const isLavender = cardStyle === "lavender";
  const isGradient = cardStyle === "gradient";
  const isSpeedCardsDesign = designVariant === "speed-cards";
  const titleTextClass = isLavender
    ? "text-neutral"
    : isSpeedCardsDesign
      ? "text-brand"
      : "text-brand-strong";
  const bodyTextClass = isDesktopNonPricePointVariant
    ? "body-sm-semibold text-brand-strong"
    : isLavender
      ? "title-sm-normal text-neutral"
      : isSpeedCardsDesign
        ? "title-sm-semibold text-neutral"
        : "title-xs-normal text-brand-strong";
  const featureTextColorClass =
    featuresColor === "black" || isSpeedCardsDesign || isLavender
      ? "text-neutral"
      : "text-brand-strong";
  const featureBodyTextClass = isLavender
    ? "title-sm-normal text-neutral md:body-xs-normal"
    : cn("title-xs-normal md:body-xs-normal", featureTextColorClass);

  if (isFeaturedGridMobileDesign) {
    const isNonPricePointFeaturedCard =
      isNonPricePointVariant && isFeaturedCard;
    const isNonPricePointNonFeaturedCard =
      isNonPricePointVariant && !isFeaturedCard;
    const shouldCenterMobileNoPricePostLegalCopy =
      isNonPricePointNonFeaturedCard &&
      hasMobileNoPricePostLegalCopy &&
      !mobileLegalDisclaimer;
    const mobileCardClassName = cn(
      "relative flex flex-col overflow-hidden rounded-lg px-2 py-3 text-left",
      isNonPricePointVariant
        ? isFeaturedCard
          ? "h-full min-h-28.5 border border-brand bg-gradient-lavender-to-white"
          : "h-full min-h-41 border border-brand bg-neutral"
        : "h-full bg-material-theme1-base",
      !isNonPricePointVariant &&
        (isFeaturedCard ? "h-28.5 bg-cover bg-center bg-no-repeat" : "h-45")
    );
    const mobileCardStyle =
      !isNonPricePointVariant && isFeaturedCard
        ? {
            backgroundImage: `url('${PRICE_PRODUCT_CARD_IMAGES.mobileCondensedBackground.src}')`,
          }
        : undefined;
    const mobileCardTitleClassName = cn(
      "leading-tight",
      isNonPricePointVariant
        ? "title-sm-semibold text-brand-strong"
        : "title-xs-semibold text-brand"
    );

    return (
      <div
        className="@container/product-card-new relative h-full w-full"
        ref={cardRef}
        data-testid={
          isFeaturedCard
            ? "featured-mobile-price-product-card"
            : isNonPricePointVariant
              ? "non-price-point-non-featured-card"
              : undefined
        }
      >
        <div className={mobileCardClassName} style={mobileCardStyle}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                {mobileFeaturedGridIcon ? (
                  <LazyIcon
                    name={mobileFeaturedGridIcon}
                    className="h-6 w-6 shrink-0"
                    style="outline"
                    theme="purple"
                  />
                ) : null}
                <h3 className={mobileCardTitleClassName}>{cardTitle}</h3>
              </div>
              {!isNonPricePointVariant ? (
                <p className="text-caption text-brand">{tagline}</p>
              ) : null}
            </div>
            {isNonPricePointFeaturedCard
              ? renderFeaturedGridVariantCtas({
                  buttonClassName: "body-sm-semibold h-9 min-w-fit px-3",
                  defaultSize: "sm",
                  wrapperClassName: "shrink-0",
                })
              : null}
            {!isNonPricePointVariant && mobileMostPopularLabel ? (
              <span className="inline-flex shrink-0 items-center rounded-md inactive-brand px-3 py-1 text-caption text-brand-strong">
                {mobileMostPopularLabel}
              </span>
            ) : null}
          </div>

          {isNonPricePointNonFeaturedCard && !hasMobileNoPricePostLegalCopy ? (
            <p className="mt-1 text-caption text-neutral">{tagline}</p>
          ) : shouldCenterMobileNoPricePostLegalCopy ? (
            <div className="flex flex-1 items-center">
              <p className="text-caption text-neutral">
                {mobileNoPricePostLegalCopy}
              </p>
            </div>
          ) : null}

          {isNonPricePointFeaturedCard &&
          mobileFeaturedGridBullets &&
          mobileFeaturedGridBullets.length > 0 ? (
            <ul
              className="mt-2 m-0 list-none space-y-1 p-0"
              data-testid="non-price-point-featured-bullets"
            >
              {mobileFeaturedGridBullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <IconCheck
                    className="mt-0.5 h-3 w-3 shrink-0"
                    style="filled"
                    theme="green"
                  />
                  <span className="text-caption text-neutral">{bullet}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {isFeaturedCard ? (
            isNonPricePointVariant ? (
              mobileLegalDisclaimer ? (
                <p className="mt-auto italic text-caption text-neutral-accent">
                  {mobileLegalDisclaimer}
                </p>
              ) : null
            ) : (
              <div className="mt-auto flex flex-col gap-2">
                {mobileLegalDisclaimer ? (
                  <p className="italic text-caption text-neutral-accent">
                    {mobileLegalDisclaimer}
                  </p>
                ) : null}
                {shouldShowPriceSection ? (
                  <div className="flex items-end justify-between gap-3">
                    <div className="flex min-w-0 items-end gap-0 text-neutral">
                      <span className="title-base-semibold self-start leading-none">
                        $
                      </span>
                      <span className="title-1-5xl-bold leading-none">
                        {Math.floor(displayPrice)}
                      </span>
                      {hasCents ? (
                        <span className="body-sm-semibold pb-1 leading-none">
                          {cents.toString().padStart(2, "0")}
                        </span>
                      ) : null}
                      <div className="mb-0.5 ml-1 flex flex-col leading-tight text-neutral">
                        <span className="body-sm-semibold">/mo</span>
                        <span className="text-caption">
                          for {mobilePricingLabel}
                        </span>
                      </div>
                    </div>
                    {renderFeaturedGridVariantCtas({
                      wrapperClassName: "shrink-0",
                    })}
                  </div>
                ) : (
                  renderFeaturedGridVariantCtas({})
                )}
              </div>
            )
          ) : (
            <div className="mt-auto flex flex-col gap-2">
              {mobileLegalDisclaimer ? (
                <p className="italic text-caption text-neutral-accent">
                  {mobileLegalDisclaimer}
                </p>
              ) : null}
              {isNonPricePointVariant ? (
                <>
                  {mobileLegalDisclaimer && hasMobileNoPricePostLegalCopy ? (
                    <p className="text-caption text-neutral">
                      {mobileNoPricePostLegalCopy}
                    </p>
                  ) : null}
                  {renderFeaturedGridVariantCtas({})}
                </>
              ) : shouldShowPriceSection ? (
                <>
                  <div className="flex items-end gap-0 text-neutral">
                    <span className="title-base-semibold self-start leading-none">
                      $
                    </span>
                    <span className="title-1-5xl-bold leading-none">
                      {Math.floor(displayPrice)}
                    </span>
                    {hasCents ? (
                      <span className="body-sm-semibold pb-1 leading-none">
                        {cents.toString().padStart(2, "0")}
                      </span>
                    ) : null}
                    <div className="ml-1 flex flex-col leading-tight text-neutral">
                      <span className="body-sm-semibold">/mo</span>
                      <span className="text-caption">
                        for {mobilePricingLabel}
                      </span>
                    </div>
                  </div>
                  {renderFeaturedGridVariantCtas({})}
                </>
              ) : (
                renderFeaturedGridVariantCtas({})
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "@container/product-card-new relative w-full h-full",
          isDesktopNonPricePointVariant && "md:min-h-100"
        )}
        ref={cardRef}
      >
        <div
          className={cn(
            "relative flex w-full flex-col h-full overflow-hidden",
            "rounded-lg",
            isGradient
              ? "bg-gradient-lavender-to-white border-1 border-brand"
              : isLavender
                ? "bg-brand-subtle border-0"
                : isSpeedCardsDesign
                  ? "bg-neutral border-0"
                  : "bg-neutral border-1 border-brand"
          )}
        >
          {topBanner ? (
            <div className="bg-dark-secondary px-3 py-2 text-center text-white">
              <div
                className="body-sm-semibold"
                dangerouslySetInnerHTML={{ __html: parseMetadata(topBanner) }}
              />
            </div>
          ) : null}
          {/* Card Content */}
          <div
            className={cn(
              "flex flex-col flex-1",
              isSpeedCardsDesign
                ? "px-6 py-10 md:px-8 md:py-14"
                : "px-6 pt-6 pb-8 md:px-5 lg:px-6"
            )}
          >
            <div className="min-h-[1px]" ref={rtbRef}>
              {/* Header - Speed/Unit or Extended Title */}
              <div
                className={
                  textAlign === "center" ? "text-center mb-2" : "text-left mb-2"
                }
              >
                <div
                  className={`flex items-start gap-3 ${textAlign === "center" ? "justify-center" : "justify-between "}`}
                >
                  <h3
                    className={cn(
                      "md:text-wrap",
                      isDesktopNonPricePointVariant
                        ? "title-lg-bold"
                        : "title-base-semibold",
                      !isDesktopNonPricePointVariant &&
                        isSpeedCardsDesign &&
                        "title-xl-bold",
                      titleTextClass
                    )}
                  >
                    {cardTitle}
                  </h3>
                  {badgeCopy ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-md bg-gradient-expressive text-white font-normal text-base text-nowrap">
                      {badgeCopy}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Variable Content Container - includes tagline + price + legalDisclaimer + RTB items for height matching */}
              <div>
                {/* Tagline */}
                <div
                  className={`mb-4 ${textAlign === "center" ? "text-center" : "text-left"}`}
                >
                  <p className={bodyTextClass}>{tagline}</p>
                </div>

                {/* Pricing Display */}
                {shouldShowPriceSection ? (
                  <div
                    className={`mb-6 ${textAlign === "center" ? "text-center" : "text-left"}`}
                  >
                    <div
                      className={cn(
                        "flex gap-0.5",
                        isSpeedCardsDesign && "items-center"
                      )}
                    >
                      {/* Dollar sign - smaller */}
                      <span
                        className={cn(
                          "inline-block text-4xl font-bold leading-none align-bottom",
                          isSpeedCardsDesign ? "text-neutral" : "text-brand"
                        )}
                      >
                        $
                      </span>
                      {/* Price number - main size */}
                      <span
                        className={cn(
                          "text-6xl font-bold leading-none",
                          isSpeedCardsDesign ? "text-neutral" : "text-brand"
                        )}
                      >
                        {Math.floor(displayPrice)}
                      </span>
                      {/* Cents - if present, same size as dollar sign */}
                      {hasCents ? (
                        <span
                          className={cn(
                            "font-bold leading-none text-display2",
                            isSpeedCardsDesign ? "text-neutral" : "text-brand"
                          )}
                        >
                          {cents.toString().padStart(2, "0")}
                        </span>
                      ) : null}
                      <div
                        className={cn(
                          "flex flex-col items-baseline",
                          isSpeedCardsDesign && "items-center self-center"
                        )}
                      >
                        {strikethroughPrice ? (
                          <>
                            {/* Strikethrough price - baseline aligned */}
                            <span className="ml-1 pt-2 text-xl text-neutral-accent font-light line-through">
                              ${strikethroughPrice}
                            </span>
                            {/* Period text - baseline aligned with strikethrough */}
                            <span
                              className={cn(
                                "ml-0.5 text-xs-semibold text-(length:--text-base)",
                                isSpeedCardsDesign
                                  ? "text-neutral"
                                  : "text-brand"
                              )}
                            >
                              /mo for{" "}
                              <span className="whitespace-nowrap">
                                {mobilePricingLabel}
                              </span>
                            </span>
                          </>
                        ) : (
                          <>
                            {/* Period text - wraps to two lines */}
                            <span
                              className={cn(
                                "ml-1 max-w-16",
                                isSpeedCardsDesign
                                  ? "title-tiny-semibold text-left"
                                  : "text-xs-semibold text-(length:--text-base)",
                                isSpeedCardsDesign
                                  ? "text-neutral"
                                  : "text-brand"
                              )}
                            >
                              /mo for{" "}
                              <span className="whitespace-nowrap">
                                {mobilePricingLabel}
                              </span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                {legalDisclaimer && (
                  <div
                    className={
                      textAlign === "center"
                        ? "text-center mb-4"
                        : "text-left mb-4"
                    }
                  >
                    <p className="italic text-body2 text-neutral-accent">
                      {legalDisclaimer}
                    </p>
                  </div>
                )}

                {/* Bundle Product Images */}
                {isLavender && bundles && bundles.length > 0 && (
                  <BundleProductImage bundles={bundles} className="my-4" />
                )}

                {/* Features */}
                {featuresIcon === "bulletpoints" ? (
                  <ul
                    className={cn(
                      "mb-6 list-disc space-y-2 text-base leading-[22px] ms-6",
                      featureBodyTextClass
                    )}
                  >
                    {features.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <div
                    className={cn(
                      isSpeedCardsDesign ? "mb-10 space-y-2" : "mb-6 space-y-2",
                      isDesktopNonPricePointVariant || isSpeedCardsDesign
                        ? "px-0"
                        : "px-2"
                    )}
                  >
                    {features.map((item, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "flex gap-2",
                          isDesktopNonPricePointVariant
                            ? "items-start"
                            : "items-center"
                        )}
                      >
                        <div className="mt-1 flex-shrink-0">
                          {isSpeedCardsDesign ? (
                            <IconCheckCircle
                              className="h-4 w-4"
                              style="filled"
                              theme="purple"
                            />
                          ) : (
                            <IconListChecked className="w-3 h-3" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p
                            className={cn(
                              "text-sm",
                              featureBodyTextClass,
                              isSpeedCardsDesign && idx === 0 && "font-semibold"
                            )}
                          >
                            {item}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CTA Buttons */}
            {ctas.length > 0 ? (
              <div
                className={cn(
                  "flex flex-col gap-2",
                  "mt-auto",
                  textAlign === "center" ? "items-center" : "items-start"
                )}
              >
                <CTAGroup
                  ctas={resolvedCtas}
                  buttonClassName="xl:whitespace-nowrap"
                  defaultDisplay="outline"
                  defaultTone={defaultOutlineCtaTone}
                  defaultSize={isSpeedCardsDesign ? "sm" : "md"}
                  orientation={
                    isSpeedCardsDesign
                      ? {
                          mobile: "vertical",
                          tablet: "vertical",
                          desktop: "vertical",
                          xl: "horizontal",
                        }
                      : "vertical"
                  }
                  alignment={textAlign === "center" ? "center" : "left"}
                  wrap={false}
                  location="productCards"
                  onModalOpen={onCtaModalClick}
                />
              </div>
            ) : null}
          </div>

          {/* Broadband Labels Section */}
          {offerId && localizationType === "full" ? (
            <>
              <div
                className={`border-stroke-theme1-base offer-label-grid-broadband-ui p-6 ${isExpanded ? "show-label" : "hide-label"}`}
              >
                <BroadbandFactsWebComponent
                  offerId={offerId}
                  sessionId={sessionId}
                  servicePlanType="POSTPAID"
                  lineOfBusiness="INTERNET"
                  onResize={handleBroadbandFactsResize}
                />
              </div>

              <div className="bg-material-neutral2-base">
                <div className="read-more-text items-center flex flex-row justify-center px-8 py-2 break-all">
                  <button
                    className="cursor-pointer no-underline text-headline3 text-xs flex items-center gap-1"
                    onClick={handleBroadbandToggle}
                    aria-label={
                      isExpanded
                        ? "Collapse broadband facts"
                        : "Expand broadband facts"
                    }
                  >
                    {isExpanded ? "Read Less" : "Read More"}
                    {isExpanded ? (
                      <IconCaretButtonUp className="w-4 h-4" />
                    ) : (
                      <IconCaretButtonDown className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PriceProductCard;
